/**
 * Push Notification Service
 * Handles browser push notifications.
 */

import { apiRequest } from '../../../lib/api';
import { IS_DEVELOPMENT, VAPID_PUBLIC_KEY } from '../../../lib/env';
import { API_PATHS } from '../../../lib/apiRoutes';
import { buildIdempotencyKey } from '../../../lib/idempotency';
import { recordClientDiagnostic } from '../../../lib/clientDiagnostics';

const pushLog = (...args: unknown[]) => {
  const message = args.map((arg) => (typeof arg === 'string' ? arg : String(arg))).join(' ');
  recordClientDiagnostic(message, undefined, 'info');
};

/**
 * Helper: Convert VAPID key to Uint8Array
 */
const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  
  return outputArray;
};

/**
 * Save push subscription to server
 */
const saveSubscriptionToServer = async (subscription: PushSubscription): Promise<void> => {
  try {
    const payload = typeof subscription.toJSON === 'function' ? subscription.toJSON() : subscription;
    const endpoint =
      payload && typeof payload === 'object' && 'endpoint' in payload
        ? String((payload as { endpoint?: string }).endpoint || '')
        : '';
    await apiRequest<void>(API_PATHS.push.subscribe, {
      method: 'POST',
      body: JSON.stringify(payload),
      idempotencyKey: buildIdempotencyKey('pushSubscribe', endpoint ? [endpoint.slice(-80)] : []),
      requireIdempotencyKey: true
    });
    
    pushLog('[Push] Subscription saved to server');
  } catch (error) {
    pushLog('[Push] Failed to save subscription:', error);
  }
};

/**
 * Remove push subscription from server
 */
const removeSubscriptionFromServer = async (subscription: PushSubscription): Promise<void> => {
    try {
        const raw = typeof subscription.toJSON === 'function' ? subscription.toJSON() : subscription;
        const endpoint =
          raw && typeof raw === 'object' && 'endpoint' in raw
            ? String((raw as { endpoint?: string }).endpoint || '')
            : '';
        await apiRequest<void>(API_PATHS.push.unsubscribe, {
            method: 'POST',
            body: JSON.stringify(raw),
            idempotencyKey: buildIdempotencyKey('pushUnsubscribe', endpoint ? [endpoint.slice(-80)] : []),
            requireIdempotencyKey: true
        });
        pushLog('[Push] Subscription removed from server');
    }
    catch (error) {
        pushLog('[Push] Failed to remove subscription:', error);
    }
};
/**
 * Check if push notifications are supported
 */
export const isPushSupported = (): boolean => {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
};

/**
 * Get current notification permission status
 */
export const getNotificationPermission = (): NotificationPermission => {
  return Notification.permission;
};

/**
 * Request permission for push notifications (works WITHOUT VAPID keys)
 * Local notifications don't require any backend setup
 */
export const requestNotificationPermission = async (): Promise<boolean> => {
  if (!isPushSupported()) {
    pushLog('[Push] Push notifications not supported in this browser');
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission === 'denied') {
    pushLog('[Push] Notification permission was denied by user');
    return false;
  }

  try {
    const permission = await Notification.requestPermission();
    pushLog('[Push] Permission result:', permission);
    return permission === 'granted';
  } catch (error) {
    pushLog('[Push] Error requesting permission:', error);
    return false;
  }
};

/**
 * Register service worker
 */
export const registerServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!('serviceWorker' in navigator)) {
    recordClientDiagnostic('[SW] Service workers not supported', undefined, 'warning');
    return null;
  }

  // Vite dev serves mutable modules; caching + clone races break HMR and flood the console.
  if (IS_DEVELOPMENT) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    } catch {
      /* ignore */
    }
    return null;
  }

  try {
    // M3 (hard launch v2): pin SW URL to current build hash so a deploy
    // forces the browser to fetch the new script and trigger
    // install/activate (which purges any stale cache).
    const buildHash = (typeof __BUILD_HASH__ !== 'undefined' && __BUILD_HASH__) || 'dev';
    const swUrl = `/sw.js?v=${encodeURIComponent(buildHash)}`;
    const registration = await navigator.serviceWorker.register(swUrl);
    pushLog('[SW] Service worker registered:', registration, 'build:', buildHash);
    return registration;
  } catch (error) {
    pushLog('[SW] Service worker registration failed:', error);
    return null;
  }
};

/**
 * Subscribe to push notifications (optional - only if VAPID keys configured)
 * Works with or without VAPID keys
 * 
 * To enable server-side push:
 * 1. Generate VAPID keys (FREE): npx web-push generate-vapid-keys
 * 2. Add to .env: VITE_VAPID_PUBLIC_KEY=your-key
 * 3. Server sends push notifications
 * 
 * Without VAPID keys: Local notifications still work perfectly
 */
export const subscribeToPush = async (): Promise<PushSubscription | null> => {
  if (!isPushSupported()) {
    pushLog('[Push] Push notifications not supported in this browser');
    return null;
  }

  try {
    // First register service worker
    const registration = await registerServiceWorker();
    if (!registration) {
      pushLog('[Push] Service worker registration failed, local notifications only');
      return null;
    }

    // Wait for service worker to be ready
    const swRegistration = await navigator.serviceWorker.ready;

    // Check if already subscribed
    let subscription = await swRegistration.pushManager.getSubscription();
    
    if (subscription) {
      pushLog('[Push] Already subscribed to push');
      return subscription;
    }

    // Request permission
    const permitted = await requestNotificationPermission();
    if (!permitted) {
      pushLog('[Push] User denied notification permission');
      return null;
    }

    // Check if VAPID key is configured
    const vapidPublicKey = VAPID_PUBLIC_KEY;
    
    if (!vapidPublicKey) {
      pushLog('[Push] No VAPID key configured - local notifications enabled, server push disabled');
      pushLog('[Push] To enable server push notifications, generate free VAPID keys:');
      pushLog('[Push]   npx web-push generate-vapid-keys');
      return null;
    }

    // Subscribe to push with VAPID key
    subscription = await swRegistration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource,
    });

    pushLog('[Push] Successfully subscribed to server push');
    
    // Send subscription to server
    await saveSubscriptionToServer(subscription);
    
    return subscription;
  } catch (error) {
    pushLog('[Push] Push subscription optional - app works without it:', error);
    return null;
  }
};

/**
 * Unsubscribe from push notifications
 */
export const unsubscribeFromPush = async (): Promise<boolean> => {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (!subscription) {
      return true;
    }

    const success = await subscription.unsubscribe();
    
    if (success) {
      pushLog('[Push] Unsubscribed successfully');
      // Remove subscription from server
      await removeSubscriptionFromServer(subscription);
    }
    
    return success;
  } catch (error) {
    pushLog('[Push] Unsubscribe failed:', error);
    return false;
  }
};

/**
 * Show a local notification (FREE - no VAPID keys needed!)
 * Works perfectly without any backend setup
 */
export const showLocalNotification = async (title: string, options?: NotificationOptions) => {
  if (!isPushSupported()) {
    pushLog('[Push] Notifications not supported in this browser');
    return;
  }

  const permitted = await requestNotificationPermission();
  if (!permitted) {
    pushLog('[Push] User notification permission not granted');
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    await registration.showNotification(title, {
      icon: '/icons/icon-192.png',
      badge: '/icons/icon-192.png',
      ...(options || {}),
    } as NotificationOptions);
    pushLog('[Push] Local notification shown:', title);
  } catch (error) {
    pushLog('[Push] Local notification error:', error);
  }
};

